import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, policies, violationMessages, type PolicyGroupDetail, type ViolationMessage } from '../../lib/api'
import { previewNotice } from '../violationMessages/violationMessages'
import { groupMessage } from './policyGroups'

interface Props {
  group: PolicyGroupDetail
  /** Called after a choice is saved, so the page reads the members again. */
  onChanged: () => void
}

/**
 * Which violation message a builder group shows (ADR-S-053), chosen once for the group.
 *
 * Without it the only way was each member's Advanced settings, one host at a time. The choice goes to
 * every member that tells the person and to no other, and like any binding it reaches devices at the
 * next publish - which goes out as a new revision when the words changed (ADR-S-054).
 */
export function GroupMessagePicker({ group, onChanged }: Props) {
  const state = groupMessage(group)
  const [messages, setMessages] = useState<ViolationMessage[] | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    violationMessages
      .list()
      .then((list) => setMessages(list.items))
      .catch((error: unknown) => setFailure(error instanceof ApiError ? error.message : 'The messages could not be read.'))
  }, [])

  if (state.notifying === 0) return null

  const choose = async (next: string | null) => {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      const saved = await policies.useGroupViolationMessage(group.groupId, next)
      const count = saved.chosen.length
      setNotice(
        next === null
          ? 'Every policy here shows its own words again, from the next publish.'
          : `Chosen for ${count === 1 ? 'the policy' : `all ${count} policies`} that tell the person. Devices show it from the next publish.`
      )
      onChanged()
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The message could not be chosen.')
    } finally {
      setBusy(false)
    }
  }

  const value = state.mixed ? 'mixed' : (state.chosen ?? '')
  const selected = messages?.find((message) => message.violationMessageId === state.chosen) ?? null
  const own = messages?.filter((message) => !message.builtIn) ?? []
  const builtIn = messages?.filter((message) => message.builtIn) ?? []

  return (
    <div className="card" style={{ marginTop: '1rem' }} data-testid="group-message">
      <div className="card-body">
        <div className="field">
          <label htmlFor="group-message-choice">What the person is told</label>
          <select
            id="group-message-choice"
            value={value}
            disabled={busy || messages === null || group.status !== 'active'}
            onChange={(event) => event.target.value !== 'mixed' && void choose(event.target.value || null)}
            data-testid="group-message-choice"
          >
            {state.mixed && <option value="mixed">Different for each policy</option>}
            <option value="">Each policy's own words</option>
            {own.length > 0 && (
              <optgroup label="Your organization's">
                {own.map((message) => (
                  <option key={message.violationMessageId} value={message.violationMessageId}>
                    {message.name}
                    {message.reference ? ` (${message.reference})` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Built in">
              {builtIn.map((message) => (
                <option key={message.violationMessageId} value={message.violationMessageId}>
                  {message.name}
                </option>
              ))}
            </optgroup>
          </select>
          <div className="hint">
            For the {state.notifying === 1 ? 'policy' : `${state.notifying} policies`} here that tell the person. Write your
            own on the <Link to="/policies?tab=messages">Messages</Link> tab.
          </div>
        </div>

        {failure && (
          <div className="banner error" role="alert">
            {failure}
          </div>
        )}
        {notice && (
          <div className="banner success" role="status" data-testid="group-message-notice">
            {notice}
          </div>
        )}
        {state.mixed && (
          <div className="banner warning" role="note" data-testid="group-message-mixed">
            These policies show different messages, chosen under their Advanced settings. Choosing one here gives them all
            the same.
          </div>
        )}
        {selected && (
          <div className="notice-preview" data-testid="group-message-preview" aria-label="What the person sees">
            <strong>{previewNotice(selected.title, selected.reference ?? '')}</strong>
            <span>{previewNotice(selected.message, selected.reference ?? '')}</span>
          </div>
        )}
      </div>
    </div>
  )
}
